import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import L from 'leaflet';
import { MapContainer } from 'react-leaflet';
import Div100vh from 'react-div-100vh';
import 'leaflet/dist/leaflet.css';
import { useNavigation } from './_NavigationContext';
import { useLang } from './_LangContext';
import { useMapContext } from './_MapContext';
import { MapOverlayUI } from './ui/components/map';
import { ViewMenu, ViewMapInfo, ViewStampcard } from './ui/views';
import { PopupLegends, PopupMapAlert, PopupStartup } from './ui/components/popups';
import HomeScreen from './_HomeScreen';
import PageHelmet from './_PageHelmet';
import { mergeClasses } from '@griffel/react';
import NotificationCookies from './_NotificationCookies';
import StampUtils from './_StampUtils';
import { 
    MapEventHandlers, 
    RasterCoordsMap, 
    MapDataService,
    MAP_MIN_ZOOM, 
    MAP_MAX_ZOOM, 
    MAP_DEFAULT_ZOOM 
} from './map';

L.RasterCoords = require('leaflet-rastercoords');

const MapApp = () =>
{
    const location = useLocation();
    const params = useParams();

    const { 
        isHomePath, 
        openHomeModal, 
        isMenuPath, 
        openMenuModal, 
        isInfoPath, 
        openInfoModal, 
        closeNavigationModal, 
        isStampcardPath,
        openStampcardModal 
    } = useNavigation();

    const { lang, isLocaleReady } = useLang();
    const { bfMapState, setBFMap, hasPinsState, setAutoCenter, hideTargetPin, isSelectedPin } = useMapContext();

    const [infoModalDatas, setInfoModalDatas] = useState([
        { data: null, isOpen: false },
        { data: null, isOpen: false }
    ]);
    const [currentInfoModalIndex, setCurrentInfoModalIndex] = useState(0);
    
    const [mapAlertData, setMapAlertData] = useState(null);

    const langRef = useRef(lang);
    const eventHandlersRef = useRef(null);
    const infoModalDatasRef = useRef(infoModalDatas);
    const currentInfoModalIndexRef = useRef(currentInfoModalIndex);

    const initialHomeOpened = isHomePath();
    const initialMenuOpened = isMenuPath();

    const [homeScreenOpened, setHomeScreenOpened] = useState(initialHomeOpened);
    const [modalMenuOpened, setModalMenuOpened] = useState(initialMenuOpened);
    const [modalLegendsOpened, setModalLegendsOpened] = useState(false);
    const [modalMapAlertOpened, setModalMapAlertOpened] = useState(false);
    const [modalStartUpOpened, setModalStartUpOpened] = useState(false);

    const [infoOpenedFromMenu, setInfoOpenedFromMenu] = useState('');

    const [stampcardOpened, setStampcardOpened] = useState(false);
    const [hasNewStamp, setHasNewStamp] = useState(() => StampUtils.hasNewStamps());

    // Initialize event handlers
    useEffect(() =>
    {
        eventHandlersRef.current = new MapEventHandlers({
            openHomeModal,
            openMenuModal,
            openInfoModal: (type, id) => handleOpenInfoModal(type, id),
            openStampcardModal,
            setModalLegendsOpened,
            setAutoCenter,
            setBFMap,
            hideTargetPin,
            setMapAlertData,
            setModalMapAlertOpened,
            bfMapState,
            stampcardOpened,
            isSelectedPin
        });
    },
    [openHomeModal, openMenuModal, openStampcardModal, setAutoCenter, setBFMap, hideTargetPin, bfMapState, stampcardOpened, isSelectedPin]);

    // Update event handlers when state changes
    useEffect(() =>
    {
        if (eventHandlersRef.current)
        {
            eventHandlersRef.current.updateBFMapState(bfMapState);
            eventHandlersRef.current.updateStampcardOpened(stampcardOpened);
        }
    },
    [bfMapState, stampcardOpened]);

    // Handle language changes for open modals - re-fetch data with new language
    useEffect(() =>
    {
        // Skip if this is the initial render, language hasn't changed, or locale isn't ready
        if (langRef.current === lang || !isLocaleReady) return;
        
        const updateOpenModalData = async () =>
        {
            // Get current modal state
            setInfoModalDatas(prevModals =>
            {
                const hasOpenModals = prevModals.some(modal => modal.isOpen);
                
                if (!hasOpenModals) return prevModals;

                // Process modals asynchronously
                (async () =>
                {
                    const updatedModals = await Promise.all(
                        prevModals.map(async (modal) =>
                        {
                            if (!modal.isOpen || !modal.data) return modal;

                            let newData;
                            const { type, id } = modal.data;

                            if (type === 'site')
                                newData = await MapDataService.getBuildingData(id, lang);
                            else if (type === 'exterior')
                                newData = await MapDataService.getExteriorData(id, lang);
                            else if (type === 'food')
                                newData = await MapDataService.getRestaurantData(id, lang);

                            if (newData)
                            {
                                newData.type = type;
                                return { ...modal, data: newData };
                            }

                            return modal;
                        })
                    );

                    setInfoModalDatas(updatedModals);
                })();

                return prevModals; // Return current state for now
            });
        };

        updateOpenModalData();

        // Update the ref to track current language
        langRef.current = lang;
    },
    [lang, isLocaleReady]);

    // Update body data-lang attribute
    useEffect(() =>
    {
        const bodyElement = document.body;
        if (bodyElement)
        {
            bodyElement.setAttribute('data-lang', lang);
        }
    },
    [lang]);

    useEffect(() =>
    {
        // Check if the startup modal should be displayed
        const showStartupModal = NotificationCookies.shouldShowAlert('startupSaveDate');

        const urlParams = new URLSearchParams(window.location.search);
        const startupParam = urlParams.get('startup');

        if (startupParam === '1') {
            setModalStartUpOpened(true);
        } else {
            setModalStartUpOpened(showStartupModal);
        }
    },
    []);

    // Update refs when state changes
    useEffect(() =>
    {
        infoModalDatasRef.current = infoModalDatas;
    },
    [infoModalDatas]);

    useEffect(() =>
    {
        currentInfoModalIndexRef.current = currentInfoModalIndex;
    },
    [currentInfoModalIndex]);

    useEffect(() =>
    {
        if (isHomePath())
            setHomeScreenOpened(true);
        else
            setHomeScreenOpened(false);
    
        if (isMenuPath())
            setModalMenuOpened(true);
        else
            setModalMenuOpened(false);
    
        if (isStampcardPath())
            setStampcardOpened(true);
        else
            setStampcardOpened(false);
    },
    [location.pathname]);

    // Stampcard Functions
    useEffect(() =>
    {
        // Check for new stamps on mount and after AR launches
        const checkNewStamps = () => {
            setHasNewStamp(StampUtils.hasNewStamps());
        };

        checkNewStamps();
        window.addEventListener('focus', checkNewStamps);
        
        return () => {
            window.removeEventListener('focus', checkNewStamps);
        };
    }, []);

    const handleUIBtnClick = (btnKey, map) =>
    {
        if (eventHandlersRef.current)
        {
            if (btnKey === 'stampcard' && !stampcardOpened)
            {
                StampUtils.markStampsViewed();
                setHasNewStamp(false);
            }
            eventHandlersRef.current.handleUIBtnClick(btnKey, map);
        }
    };

    const handlePinClick = (type, id) =>
    {
        if (eventHandlersRef.current)
        {
            if (type === 'site' || type === 'food' || type === 'exterior')
            {
                // Force reopen even if same pin by clearing modal state first
                setInfoModalDatas(prev => prev.map(modal => ({ ...modal, isOpen: false })));
                openInfoModal(type, id);
                setInfoOpenedFromMenu('');
            }
            else
            {
                eventHandlersRef.current.handlePinClick(type, id);
            }
        }
    };

    const handleMenuPinClick = useCallback((type, item) =>
    {
        closeNavigationModal();
        
        // Import UserLocation here to avoid circular dependencies
        import('./_UserLocation').then(({ UserLocation }) =>
        {
            UserLocation.getInstance().setTargetPin(type, item);
            UserLocation.getInstance().highlightPin(type, item);
        });
    },
    [closeNavigationModal]);

    const handleMenuInfoClick = (type, item) =>
    {
        if (type === 'site' || type === 'food' || type === 'exterior')
        {
            openInfoModal(type, item.id);
        }

        setInfoOpenedFromMenu(type);
    };

    const handleMenuARClick = (type, item) =>
    {
        if (type === 'site' || type === 'food' || type === 'exterior')
        {
            openInfoModal(type, item.id);
        }

        setInfoOpenedFromMenu(type);
    };

    const handleOpenInfoModal = useCallback(async (infoType, id, modalIndex = -1) =>
    {
        let data;
        
        if (infoType === 'site')
            data = await MapDataService.getBuildingData(id, lang);
        else if (infoType === 'exterior')
            data = await MapDataService.getExteriorData(id, lang);
        else if (infoType === 'food')
            data = await MapDataService.getRestaurantData(id, lang);
    
        if (!data) return;
    
        data.type = infoType;
    
        // Determine the next index for the modal to update
        if (modalIndex === -1) modalIndex = (currentInfoModalIndex + 1) % 2;
    
        // Update the data of the next modal and open it
        setInfoModalDatas(prevData => prevData.map((modal, index) =>
            index === modalIndex ? { data, isOpen: true } : { ...modal, isOpen: false }
        ));
    
        setCurrentInfoModalIndex(modalIndex);
    
        if (isSelectedPin(infoType, id))
        {
            hideTargetPin();
        }
    },
    [currentInfoModalIndex, isSelectedPin, hideTargetPin, lang]);
    
    const handleCloseInfoModal = useCallback(() =>
    {
        setInfoModalDatas(prev => prev.map(modal => ({ ...modal, isOpen: false })));
    
        if (infoOpenedFromMenu !== '')
        {
            const params = {};
    
            if (infoOpenedFromMenu === 'site')
                params.tab = 1;
            else if (infoOpenedFromMenu === 'food')
                params.tab = 3;
    
            openMenuModal(params);
            setInfoOpenedFromMenu('');
        }
    },
    [infoOpenedFromMenu, openMenuModal]);

    // Handle info modal path changes - moved here after function definitions
    useEffect(() =>
    {
        const infoType = isInfoPath();
    
        if (infoType && isLocaleReady)
        {
            const id = parseInt(params.id);
            // Only reopen modal if path changed, not just search params
            if (!infoModalDatasRef.current.some(modal => modal.isOpen) || 
                !infoModalDatasRef.current[currentInfoModalIndexRef.current].data ||
                infoModalDatasRef.current[currentInfoModalIndexRef.current].data.id !== id)
            {
                handleOpenInfoModal(infoType, id);
            }
        }
        else if (!infoType)
        {
            handleCloseInfoModal();
        }
    },
    [location.pathname, params.id, isLocaleReady, isInfoPath, handleOpenInfoModal, handleCloseInfoModal]);

    const closeLegendsModal = () =>
    {
        setModalLegendsOpened(false);
    };

    const closeMapAlertModal = () =>
    {
        setModalMapAlertOpened(false);
    };

    const closeStartUpModal = () =>
    {
        NotificationCookies.markAlertShown('startupSaveDate');
        setModalStartUpOpened(false);
    };

    // Determine which modal is currently open to set data-modal-id attribute
    let modalID = '';
    if (modalMenuOpened) modalID = 'menu';
    else if (infoModalDatas.length > 0) modalID = "info";

    const mapClassName = mergeClasses('map-app', bfMapState ? 'is-bf-map' : null, hasPinsState === 0 ? 'no-pins' : null);

    return (
        <>
            <PageHelmet lang={lang} location={location} id={params.id} />
            <Div100vh id="map" className={mapClassName} data-lang={lang} {...(modalID && { 'data-modal-id': modalID })}>
                <MapContainer
                    id="map-container"
                    zoom={MAP_DEFAULT_ZOOM}
                    style={{ width: '100%', height: '100svh' }}
                    crs={L.CRS.EPSG3857}
                    minZoom={MAP_MIN_ZOOM}
                    maxZoom={MAP_MAX_ZOOM}
                    attributionControl={false}
                    zoomControl={false}
                >
                    <RasterCoordsMap onPinClick={handlePinClick} />
                    <MapOverlayUI
                        onUIBtnClick={handleUIBtnClick}
                        minZoom={MAP_MIN_ZOOM}
                        maxZoom={MAP_MAX_ZOOM}
                        modalMenuOpened={modalMenuOpened}
                        hasNewStamp={hasNewStamp}
                        infoModalOpen={infoModalDatas.some(modal => modal.isOpen)}
                        stampcardOpen={stampcardOpened}
                    />
                </MapContainer>
                {infoModalDatas.map((modal, index) => (
                    <ViewMapInfo key={index} isOpen={modal.isOpen} onClose={closeNavigationModal} data={modal.data} />
                ))}
                <HomeScreen isOpen={homeScreenOpened} onClose={closeNavigationModal} />
                <ViewMenu isOpen={modalMenuOpened} onClose={closeNavigationModal} onPinClick={handleMenuPinClick} onInfoClick={handleMenuInfoClick} onARClick={handleMenuARClick} />
                <PopupLegends isOpen={modalLegendsOpened} onClose={closeLegendsModal} />
                <PopupMapAlert isOpen={modalMapAlertOpened} onClose={closeMapAlertModal} messageData={mapAlertData} />
                <PopupStartup isOpen={modalStartUpOpened} onClose={closeStartUpModal} />
                <ViewStampcard 
                    isOpen={stampcardOpened} 
                    onClose={closeNavigationModal}
                    stampcardStats={StampUtils.getStampStats().stamps}
                />
            </Div100vh>
        </>
    );
};

export default MapApp; 